// src/lib/commissions.ts
import { supabase } from '../services/supabaseClient';

export type CommissionProductType = 'combo' | 'forfait' | 'carte_universelle' | 'last_call' | 'prevente' | 'boutique' | string;

export type CommissionSetting = {
  id: string;
  product_type: CommissionProductType;
  label: string;
  commission_rate: number;
  fixed_fee_cents?: number | null;
  is_active: boolean;
  legal_note?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type CommissionEvent = {
  id: string;
  source_type: CommissionProductType;
  source_id?: string | null;
  business_id?: string | null;
  merchant_id?: string | null;
  customer_email?: string | null;
  gross_amount_cents: number;
  commission_rate: number;
  fixed_fee_cents: number;
  commission_amount_cents: number;
  net_amount_cents: number;
  currency: string;
  status: 'estimated' | 'pending' | 'captured' | 'refunded' | 'cancelled' | string;
  metadata?: Record<string, any>;
  created_at: string;
};

export type MerchantRevenueSummary = {
  ok: boolean;
  totals: {
    gross_amount_cents: number;
    commission_amount_cents: number;
    net_amount_cents: number;
    estimated_count: number;
    captured_count: number;
  };
  events: CommissionEvent[];
  settings: CommissionSetting[];
};

export function formatMoneyCents(value?: number | null, currency = 'CAD') {
  const cents = Number(value || 0);
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency }).format(cents / 100);
}

export function computeCommissionCents(grossCents: number, rate: number, fixedFeeCents = 0) {
  const gross = Math.max(0, Math.round(Number(grossCents || 0)));
  const pct = Math.max(0, Number(rate || 0));
  const fixed = Math.max(0, Math.round(Number(fixedFeeCents || 0)));
  const commission = Math.round(gross * (pct / 100)) + fixed;
  return {
    gross_amount_cents: gross,
    commission_rate: pct,
    fixed_fee_cents: fixed,
    commission_amount_cents: commission,
    net_amount_cents: Math.max(0, gross - commission),
  };
}

export async function fetchMerchantRevenueDashboard(businessId: string, days = 90): Promise<MerchantRevenueSummary> {
  const { data, error } = await supabase.functions.invoke('merchant-revenue-dashboard', {
    body: { business_id: businessId, days },
  });
  if (error) throw error;
  return data as MerchantRevenueSummary;
}

export async function fetchAdminCommissionDashboard(days = 90) {
  const { data, error } = await supabase.functions.invoke('admin-commission-dashboard', {
    body: { days },
  });
  if (error) throw error;
  return data;
}

export async function updateCommissionSetting(payload: Partial<CommissionSetting> & { product_type: string }) {
  const { data, error } = await supabase.functions.invoke('commission-settings', {
    body: { action: 'upsert', setting: payload },
  });
  if (error) throw error;
  return data;
}
